import { defineStore } from 'pinia';
import api from '@/api/api';
import type { ZRating } from '@schemas/rating.schemas';
import type {Id} from "@typesDef/general.types";
import {setIdsUnique} from "@/stores/helpers/storeHelper";
import {RatingType} from "@typesDef/rating.types";

// FIXME I got unpopulated ratings for profiles (I think it is because I fetch them before with my routes
//  and the land in ratingsById (unpopulated) and they were not merged

export const useRatingStore = defineStore('rating', {
    state: () => ({
        ratingsById: {} as Record<Id, ZRating>,
        // my own rating
        myRatingId: null as Id | null,
        myRatingReceivedIds: [] as Id[],
        myRatingGivenIds: [] as Id[],
        // public ratings for events and profiles, we only load them once
        publicRatingByEventIds: {} as Record<Id, Id[]>,
        publicRatingByProfileIds: {} as Record<Id, Id[]>,
    }),
    getters: {
        /**
         * Get my own rating, this is used in the context of the profile and
         * event details page to show the current user's rating if it exists
         * show the current user's rating for that specific profile or event
         */
        myRating(state): ZRating | null {
            return state.myRatingId ? state.ratingsById[state.myRatingId] ?? null : null;
        },
        /**
         * Get ratings received for the current user, this is used in the context
         * of the profile details page to show the ratings received by the user
         */
        myRatingsReceived(state): ZRating[] {
            return state.myRatingReceivedIds.map(id => state.ratingsById[id]).filter(Boolean) as ZRating[];
        },
        /**
         * Get ratings given by the current user, this is used in the context
         * of the profile details page to show the ratings given by the user
         */
        myRatingsGiven(state): ZRating[] {
            return state.myRatingGivenIds.map(id => state.ratingsById[id]).filter(Boolean) as ZRating[];
        },
        /**
         *  Get public ratings for a specific type (event or profile) and id,
         *  this is used in the context of the profile and event details page
         *  to show the ratings for that specific profile or event
         */
        getPublicRatingsByType(state): (ratingType: RatingType, typeId: string) => ZRating[] | null {
            return (ratingType: RatingType, typeId: string) => {
                if(ratingType === RatingType.Profile) {
                    const ratingIds = state.publicRatingByProfileIds[typeId];
                    if (ratingIds) {
                        return ratingIds.map(id => state.ratingsById[id]).filter(Boolean) as ZRating[];
                    }
                } else if(ratingType === RatingType.Event) {
                    const ratingIds = state.publicRatingByEventIds[typeId];
                    if (ratingIds) {
                        return ratingIds.map(id => state.ratingsById[id]).filter(Boolean) as ZRating[];
                    }
                }

                return null;
            }
        },

        getPublicRatingsByEvent(state): (eventId: string) => ZRating[] | null {
            return (eventId: string) => {
                return this.getPublicRatingsByType(RatingType.Event, eventId);
            }
        },
        getPublicRatingsByProfile(state): (profileId: string) => ZRating[] | null {
            return (profileId: string) => {
                return this.getPublicRatingsByType(RatingType.Profile, profileId);
            }
        },

        /**
         * Get my current rating for a specific type (event or profile) and id,
         * this is used in the context of the profile and event details page to
         * show the current user's rating for that specific profile or event
         */
        getMyRatingByType(state): (ratingType: RatingType, typeId: string) => ZRating | null {
            return (ratingType: RatingType, typeId: string) => {
                if (state.myRatingId) {
                    const foundRating = state.ratingsById[state.myRatingId];
                    if(
                        foundRating && (
                            ratingType === RatingType.Profile && foundRating?.userId === typeId ||
                            ratingType === RatingType.Event && foundRating?.eventId === typeId
                        )
                    ) {
                        return foundRating;
                    }
                }
                return null;
            }
        },

        getMyRatingByProfile(state): (profileId: string) => ZRating | null {
            return (profileId: string) => {
                return this.getMyRatingByType(RatingType.Profile, profileId);
            }
        },
        getMyRatingByEvent(state): (eventId: string) => ZRating | null {
            return (eventId: string) => {
                return this.getMyRatingByType(RatingType.Event, eventId);
            }
        }
    },
    actions: {
        clearMyData(): void {
            this.ratingsById = {};
            this.myRatingId = null;
            this.myRatingReceivedIds = [];
            this.myRatingGivenIds = [];
            this.publicRatingByEventIds = {};
            this.publicRatingByProfileIds = {};
        },
        // FIXME move them to general library since they are used in multiple stores
        upsertMany(docs: ZRating[]) {
            for (const e of docs) this.ratingsById[e._id] = e
        },
        /**
         * Fetch ratings received for the current user
         */
        async fetchMyRatingsReceived(): Promise<ZRating[]> {
            const docs = await api.getDocuments<ZRating>('ratings/my/receiver');
            this.upsertMany(docs);
            setIdsUnique(this.myRatingReceivedIds, docs.map(d => d._id));
            return docs;
        },

        /**
         * Fetch ratings given by the current user
         * NOTE: Not used atm.
         */
        async fetchMyRatingsGiven(): Promise<ZRating[]> {
            const docs = await api.getDocuments<ZRating>('ratings/my/author');
            this.upsertMany(docs);
            setIdsUnique(this.myRatingGivenIds, docs.map(d => d._id));
            return docs;
        },

        /**
         * Fetch my rating for a specific profile
         * @param profileId
         */
        async fetchMyRatingForProfile(profileId: string): Promise<ZRating> {
            const foundRating = this.getMyRatingByType(RatingType.Profile, profileId);
            if(foundRating) {
                this.myRatingId = foundRating._id??null;
                return foundRating;
            }
            const doc = await api.getDocument<ZRating>('ratings/my/author/profile', profileId);
            if (doc) {
                this.upsertMany([doc]);
                this.myRatingId = doc._id??null;
                if (!this.myRatingGivenIds.includes(doc._id)) {
                    this.myRatingGivenIds.push(doc._id);
                }
            }
            return doc;
        },
        /**
         * Fetch my rating for a specific event
         * @param eventId
         */
        async fetchMyRatingForEvent(eventId: string): Promise<ZRating> {
            const foundRating = this.getMyRatingByType(RatingType.Event, eventId);
            if(foundRating) {
                this.myRatingId = foundRating._id??null;
                return foundRating;
            }

            const doc = await api.getDocument<ZRating>('ratings/my/author/event', eventId);
            if (doc) {
                this.upsertMany([doc]);
                this.myRatingId = doc._id ?? null;
                if (!this.myRatingGivenIds.includes(doc._id)) {
                    this.myRatingGivenIds.push(doc._id);
                }
            }
            return doc;
        },

        /**
         * Fetch ratings for a specific event
         * @param eventId
         */
        async fetchRatingsForEvent(eventId: string): Promise<ZRating[]> {
            const found = this.getPublicRatingsByType(RatingType.Event, eventId);
            if(found) {
                return found;
            }
            const docs = await api.getDocuments<ZRating>('ratings/event/' + eventId);
            this.upsertMany(docs);
            if(!this.publicRatingByEventIds[eventId]) {
                this.publicRatingByEventIds[eventId] = [];
            }
            setIdsUnique(this.publicRatingByEventIds[eventId], docs.map(d => d._id));
            return docs;
        },
        /**
         * Fetch latest ratings for a specific user
         * @param profileId
         */
        async fetchRatingsForProfile(profileId: string): Promise<ZRating[]> {
            const found =
                this.getPublicRatingsByType(RatingType.Profile, profileId);
            if(found) {
                return found;
            }
            const docs =
                await api.getDocuments<ZRating>('ratings/profile/' + profileId);
            this.upsertMany(docs);
            if(!this.publicRatingByProfileIds[profileId]) {
                this.publicRatingByProfileIds[profileId] = [];
            }
            setIdsUnique(this.publicRatingByProfileIds[profileId], docs.map(d => d._id));
            return docs;
        },
        /**
         * Add a new rating
         * There is no update only post.
         * @param rating
         * @returns {Promise<ZRating>}
         */
        async addRating(rating: Partial<ZRating>): Promise<ZRating> {
            const created =
                await api.postDocument<ZRating>('ratings', rating);
            if(!created || !created._id) {
                throw new Error('Failed to create rating');
            }
            this.ratingsById[created._id] = created;
            if(created.eventId) {
                if(!this.publicRatingByEventIds[created.eventId]) {
                    this.publicRatingByEventIds[created.eventId] = [];
                }
                if(!this.publicRatingByEventIds[created.eventId].includes(created._id)) {
                    this.publicRatingByEventIds[created.eventId].unshift(created._id);
                }
            }
            if(created.userId) {
                if(!this.publicRatingByProfileIds[created.userId]) {
                    this.publicRatingByProfileIds[created.userId] = [];
                }
                if(!this.publicRatingByProfileIds[created.userId].includes(created._id)) {
                    this.publicRatingByProfileIds[created.userId].unshift(created._id);
                }
            }
            // addRating only used for creating new ratings, so we can assume that
            // the created rating is given by the user
            if(!this.myRatingGivenIds.includes(created._id)) {
                this.myRatingGivenIds.unshift(created._id);
            }
            this.myRatingId = created._id;
            return created;
        },

        /**
         * Remove a rating
         * @param ratingId
         */
        async removeRating(ratingId: Id): Promise<void> {
            await api.deleteDocument<ZRating>('ratings', ratingId);
            // remove from ratingsById
            delete this.ratingsById[ratingId];
            // remove from publicRatingIds
            this.myRatingReceivedIds = this.myRatingReceivedIds.filter(id => id !== ratingId);
            // remove from myRatingGivenIds
            this.myRatingGivenIds = this.myRatingGivenIds.filter(id => id !== ratingId);
            // if the removed rating was the current user's rating, reset myRatingId
            if(this.myRatingId === ratingId) {
                this.myRatingId = null;
            }

            if(this.publicRatingByEventIds) {
                for (const eventId in this.publicRatingByEventIds) {
                    this.publicRatingByEventIds[eventId] =
                        this.publicRatingByEventIds[eventId].filter(id => id !== ratingId);
                }
            }
            if(this.publicRatingByProfileIds) {
                for (const profileId in this.publicRatingByProfileIds) {
                    this.publicRatingByProfileIds[profileId] =
                        this.publicRatingByProfileIds[profileId].filter(id => id !== ratingId);
                }
            }
        }
    }
});
